/**
 * Module-level singleton cache for dealer employees.
 *
 * - Keyed by dealerId to support multi-dealer sessions.
 * - Uses in-flight promise deduplication: concurrent callers for the same
 *   dealer all await the same Promise, so only one HTTP request sequence runs.
 * - Paginates through all pages (limit=100) and aggregates results.
 * - Cache lifetime = one browser page load.
 */

import type {
  DtoDealerEmployee,
  getDealerPortalV1DealerIdEmployeesResponse,
} from "@porchpass/api-client";

// Module-level state — keyed by dealerId to support multi-dealer sessions
const cachedEmployees: Map<string, DtoDealerEmployee[]> = new Map();
const loadPromise: Map<string, Promise<DtoDealerEmployee[]>> = new Map();

type FetchFn = (
  offset: number,
  limit: number,
) => Promise<getDealerPortalV1DealerIdEmployeesResponse>;

export async function ensureEmployeesLoaded(
  dealerId: string,
  fetchFn: FetchFn,
): Promise<DtoDealerEmployee[]> {
  // Return cached data immediately if available
  const cached = cachedEmployees.get(dealerId);
  if (cached) return cached;

  // Return in-flight Promise if a load is already happening for this dealer
  const inFlight = loadPromise.get(dealerId);
  if (inFlight) return inFlight;

  // Start a single paginated load for this dealer.
  // Error handling is inside the async IIFE — avoid chaining .catch() on
  // a separately-return'd promise, which creates an unhandled rejection.
  const promise = (async () => {
    try {
      const PAGE_SIZE = 100;
      let allEmployees: DtoDealerEmployee[] = [];
      let offset = 0;
      let total = Infinity;

      while (offset < total) {
        const response = await fetchFn(offset, PAGE_SIZE);
        if (response.status !== 200) {
          throw new Error(
            `Failed to load employees for dealer ${dealerId}: received status ${response.status}`,
          );
        }

        allEmployees = allEmployees.concat(response.data.items ?? []);
        total = response.data.total ?? allEmployees.length;
        offset += PAGE_SIZE;
      }

      cachedEmployees.set(dealerId, allEmployees);
      return allEmployees;
    } catch (error) {
      // On error, clear cached data for this dealer so callers can retry
      // instead of reusing an empty/partial result.
      cachedEmployees.delete(dealerId);
      throw error;
    } finally {
      // Always clear the in-flight promise once the load settles.
      loadPromise.delete(dealerId);
    }
  })();

  loadPromise.set(dealerId, promise);

  return promise;
}

// Synchronous lookups (must be called after ensureLoaded resolves for the dealer)
export function getEmployeeById(
  dealerId: string,
  userId: string,
): DtoDealerEmployee | undefined {
  return cachedEmployees.get(dealerId)?.find((e) => e.id === userId);
}

export function getEmployeeUser(
  dealerId: string,
  userId: string,
): { firstName?: string; lastName?: string; email?: string } | null {
  const employee = getEmployeeById(dealerId, userId);
  if (!employee) return null;
  return {
    firstName: employee.first_name,
    lastName: employee.last_name,
    email: employee.email,
  };
}

export function isLoaded(dealerId: string): boolean {
  return cachedEmployees.has(dealerId);
}
